import React, { useState } from "react";
import { ClipboardList, MessageSquare, School, Phone, Calendar, Check, X } from "lucide-react";
import api from "../../lib/api";

const steps = [
  { icon: MessageSquare, title: "1. Inquire", desc: "Send us a quick message or call. We'll share program details and answer your questions." },
  { icon: School, title: "2. Visit", desc: "Schedule a school visit to meet the teachers and see your child's future classroom." },
  { icon: ClipboardList, title: "3. Apply", desc: "Fill in the simple admission form and submit a few documents." },
  { icon: Phone, title: "4. Welcome", desc: "Once enrolled, we'll guide you through orientation and the first joyful weeks." },
];

const TIME_SLOTS = [
  "10:00 AM", "11:00 AM", "12:00 PM",
  "1:00 PM", "2:00 PM", "3:00 PM",
];

const Admissions = () => {
  const [show, setShow] = useState(false);

  return (
    <section id="admissions" className="py-16 sm:py-24" data-testid="admissions-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <span className="text-brand-blue font-heading font-bold tracking-wider uppercase text-xs">Admissions</span>
          <h2 className="font-heading text-3xl sm:text-4xl font-bold mt-2">Joining is simple</h2>
          <p className="mt-3 text-[color:var(--text-secondary)]">
            Limited seats every term. Reach out early to secure your child's place.
          </p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {steps.map((s, i) => (
            <div key={i} className="card-soft p-6 bg-white relative" data-testid={`admissions-step-${i}`}>
              <div className="absolute -top-4 -right-4 w-12 h-12 rounded-full bg-brand-yellow text-[color:var(--text-primary)] flex items-center justify-center font-heading font-bold shadow-md">
                {i + 1}
              </div>
              <div className="w-12 h-12 rounded-2xl bg-light-blue flex items-center justify-center mb-4">
                <s.icon className="text-brand-blue" size={22} />
              </div>
              <h3 className="font-heading font-bold mb-2">{s.title}</h3>
              <p className="text-sm text-[color:var(--text-secondary)] leading-relaxed">{s.desc}</p>
            </div>
          ))}
        </div>

        <div className="mt-12 bg-brand-blue rounded-[2.5rem] p-8 sm:p-12 text-white relative overflow-hidden">
          <div className="absolute -top-16 -right-10 w-48 h-48 rounded-full bg-brand-yellow/30 blur-2xl" />
          <div className="relative grid md:grid-cols-3 gap-6 items-center">
            <div className="md:col-span-2">
              <h3 className="font-heading text-2xl sm:text-3xl font-bold">Admissions open for 2026–27</h3>
              <p className="mt-2 text-white/85">
                Schedule a friendly school visit and meet our teachers — pick a time that works for you.
              </p>
            </div>
            <div className="flex md:justify-end">
              <button
                onClick={() => setShow(true)}
                className="btn-secondary"
                data-testid="admissions-book-visit-btn"
              >
                <Calendar size={18} /> Book a School Visit
              </button>
            </div>
          </div>
        </div>
      </div>

      {show && <BookVisitModal onClose={() => setShow(false)} timeSlots={TIME_SLOTS} />}
    </section>
  );
};

const BookVisitModal = ({ onClose, timeSlots }) => {
  const [form, setForm] = useState({
    parent_name: "", child_name: "", child_age: "",
    email: "", phone: "",
    preferred_date: "", preferred_time: timeSlots[0],
    notes: "",
  });
  const [status, setStatus] = useState({ state: "idle", error: "" });
  const onChange = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setStatus({ state: "submitting", error: "" });
    try {
      await api.post("/visits", form);
      setStatus({ state: "success", error: "" });
    } catch (err) {
      const d = err?.response?.data?.detail;
      const msg = typeof d === "string" ? d : Array.isArray(d) ? d.map((x) => x.msg).join(" ") : "Something went wrong";
      setStatus({ state: "error", error: msg });
    }
  };

  const today = new Date().toISOString().split("T")[0];

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-[color:rgba(15,29,58,0.55)] backdrop-blur-sm" onClick={onClose} data-testid="visit-modal-overlay">
      <div className="relative w-full max-w-xl bg-white rounded-[2rem] shadow-[0_30px_80px_rgba(0,0,0,0.30)] p-6 sm:p-8 max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="visit-modal">
        <button onClick={onClose} className="absolute top-4 right-4 w-9 h-9 rounded-full bg-cream hover:bg-light-blue flex items-center justify-center" aria-label="Close" data-testid="visit-modal-close">
          <X size={18} />
        </button>

        {status.state === "success" ? (
          <div className="py-8 text-center" data-testid="visit-success">
            <div className="w-16 h-16 rounded-full bg-light-green text-[#1d6b32] flex items-center justify-center mx-auto mb-4">
              <Check size={28} />
            </div>
            <h3 className="font-heading text-2xl font-bold mb-2">Thank you!</h3>
            <p className="text-[color:var(--text-secondary)] mb-6">
              Your school visit request has been received. We'll reach out to confirm your slot shortly.
            </p>
            <button onClick={onClose} className="btn-primary" data-testid="visit-success-close">Done</button>
          </div>
        ) : (
          <>
            <div className="flex items-center gap-3 mb-5">
              <div className="w-12 h-12 rounded-2xl bg-light-blue flex items-center justify-center">
                <Calendar size={22} className="text-brand-blue" />
              </div>
              <div>
                <h3 className="font-heading text-2xl font-bold leading-tight">Book a School Visit</h3>
                <p className="text-sm text-[color:var(--text-secondary)]">Pick a date and time that works for you.</p>
              </div>
            </div>

            <form onSubmit={submit} className="space-y-4" data-testid="visit-form">
              <div className="grid sm:grid-cols-2 gap-3">
                <Field label="Parent name" required>
                  <input required value={form.parent_name} onChange={onChange("parent_name")} className="visit-input" data-testid="visit-parent-name" />
                </Field>
                <Field label="Email" required>
                  <input required type="email" value={form.email} onChange={onChange("email")} className="visit-input" data-testid="visit-email" />
                </Field>
                <Field label="Child name" required>
                  <input required value={form.child_name} onChange={onChange("child_name")} className="visit-input" data-testid="visit-child-name" />
                </Field>
                <Field label="Child age" required>
                  <input required placeholder="e.g. 3 years" value={form.child_age} onChange={onChange("child_age")} className="visit-input" data-testid="visit-child-age" />
                </Field>
                <Field label="Phone">
                  <input value={form.phone} onChange={onChange("phone")} className="visit-input" data-testid="visit-phone" />
                </Field>
                <Field label="Preferred date" required>
                  <input required type="date" min={today} value={form.preferred_date} onChange={onChange("preferred_date")} className="visit-input" data-testid="visit-date" />
                </Field>
              </div>
              <Field label="Preferred time" required>
                <div className="grid grid-cols-3 sm:grid-cols-6 gap-2" role="radiogroup" aria-label="Time slots">
                  {timeSlots.map((t) => (
                    <button
                      key={t} type="button"
                      onClick={() => setForm((p) => ({ ...p, preferred_time: t }))}
                      data-testid={`visit-slot-${t.replace(/[:\s]/g, "")}`}
                      className={`py-2 px-2 rounded-full text-xs font-bold border-2 transition ${form.preferred_time === t ? "bg-brand-blue text-white border-brand-blue" : "bg-cream border-transparent hover:border-brand-blue text-[color:var(--text-primary)]"}`}
                    >
                      {t}
                    </button>
                  ))}
                </div>
              </Field>
              <Field label="Notes">
                <textarea rows={3} value={form.notes} onChange={onChange("notes")} placeholder="Anything we should know?" className="visit-input resize-none" data-testid="visit-notes" />
              </Field>

              {status.state === "error" && (
                <div className="bg-red-50 text-red-700 rounded-2xl px-4 py-3 text-sm" data-testid="visit-error">{status.error}</div>
              )}

              <button type="submit" disabled={status.state === "submitting"} className="btn-primary w-full justify-center" data-testid="visit-submit">
                {status.state === "submitting" ? "Sending..." : "Request Visit"}
              </button>
            </form>
          </>
        )}

        <style>{`
          .visit-input { width:100%; background: var(--bg-cream); border:1px solid #e6e2d8; border-radius:0.85rem; padding:0.65rem 0.9rem; font-family:'Nunito',sans-serif; font-size:0.92rem; color: var(--text-primary); }
          .visit-input:focus { outline:none; background:white; border-color: var(--brand-blue); }
        `}</style>
      </div>
    </div>
  );
};

const Field = ({ label, required, children }) => (
  <label className="block">
    <span className="block text-xs font-semibold text-[color:var(--text-primary)] mb-1">
      {label} {required && <span className="text-brand-blue">*</span>}
    </span>
    {children}
  </label>
);

export default Admissions;
